// v2/components/admin/OrderDeleteModal.js — siparis silme onayi
// Acilinca onizleme ister (hicbir sey silinmez). Transfer edilmis sipariste
// silme butonu, siparis numarasi elle yazilana kadar kapali kalir.
// Kabuk stilleri UserFormModal ile ortak (s), silmeye ozel alanlar (d).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Trash2, Loader2, AlertTriangle, ShieldAlert, Info } from 'lucide-react';
import { siparisSil } from '../../hooks/useAdminOrders';
import { formatTRY, formatNumber } from '../../utils/format';
import s from './UserFormModal.module.css';
import d from './OrderDeleteModal.module.css';

const TABLOLAR = [
  ['rkn_sepet', 'Sipariş satırları'],
  ['siparis_kuyruk', 'Aktarım kuyruğu'],
  ['siparis_transfer_durum', 'Aktarım durumu'],
  ['siparis_transfer_log', 'Aktarım geçmişi'],
];

export default function OrderDeleteModal({ siparis, onKapat, onSilindi }) {
  const acik = Boolean(siparis);
  const [oz, setOz] = useState(null);
  const [yukHata, setYukHata] = useState(null);
  const [hata, setHata] = useState(null);
  const [yazilan, setYazilan] = useState('');
  const [siliyor, setSiliyor] = useState(false);
  const inputRef = useRef(null);

  // onizleme
  useEffect(() => {
    if (!acik) return undefined;
    let iptal = false;
    setOz(null);
    setYukHata(null);
    setHata(null);
    setYazilan('');
    setSiliyor(false);
    siparisSil(siparis.order_nos)
      .then((v) => {
        if (iptal) return;
        if (!v || !v.success || !v.preview) throw new Error((v && v.message) || 'Sipariş bilgisi alınamadı');
        setOz(v.preview);
      })
      .catch((e) => { if (!iptal) setYukHata((e && e.message) || 'Sipariş bilgisi alınamadı'); });
    return () => { iptal = true; };
  }, [acik, siparis]);

  useEffect(() => {
    if (oz && (oz.transferred || oz.in_transfer) && inputRef.current) inputRef.current.focus();
  }, [oz]);

  useEffect(() => {
    if (!acik) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !siliyor) onKapat(); };
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = onceki;
      document.removeEventListener('keydown', esc);
    };
  }, [acik, siliyor, onKapat]);

  const kritik = Boolean(oz) && (oz.transferred || oz.in_transfer);
  const numaraTamam = Boolean(oz) && (!kritik || oz.nos.includes(yazilan.trim()));

  const sil = useCallback(async () => {
    if (!oz || siliyor || !numaraTamam) return;
    setHata(null);
    setSiliyor(true);
    try {
      const v = await siparisSil(oz.nos, true, yazilan.trim());
      if (!v || !v.success) throw new Error((v && v.message) || 'Sipariş silinemedi');
      onSilindi(v.message);
    } catch (e) {
      setHata((e && e.message) || 'Sipariş silinemedi');
      setSiliyor(false);
    }
  }, [oz, siliyor, numaraTamam, yazilan, onSilindi]);

  if (!acik) return null;
  const nolar = siparis.order_nos || [];
  const ilkNo = siparis.order_no || nolar[0] || '';

  return (
    <div className={s.overlay} role="dialog" aria-modal="true" aria-label="Siparişi sil">
      <div className={s.backdrop} onClick={() => !siliyor && onKapat()} aria-hidden="true" />
      <div className={s.modal}>
        <div className={s.head}>
          <div className={s.headText}>
            <h3 className={s.title}>Siparişi Sil</h3>
            <p className={s.sub}>
              {siparis.title || siparis.code} · <b>{ilkNo}</b>
              {nolar.length > 1 ? ` +${nolar.length - 1}` : ''}
            </p>
          </div>
          <button type="button" className={s.closeBtn} onClick={onKapat}
                  disabled={siliyor} aria-label="Kapat">
            <X size={18} />
          </button>
        </div>

        <div className={s.body}>
          {!oz && !yukHata && (
            <div className={d.load}><Loader2 size={16} className={s.spin} /> Sipariş bilgileri alınıyor…</div>
          )}
          {yukHata && (
            <div className={s.error}><AlertTriangle size={16} /> <span>{yukHata}</span></div>
          )}

          {oz && (
            <>
              {hata && (
                <div className={s.error}><AlertTriangle size={16} /> <span>{hata}</span></div>
              )}

              <div className={d.summary}>
                <span className={d.k}>Müşteri</span>
                <span className={d.v}>{oz.title || oz.code} <i>({oz.code})</i></span>
                <span className={d.k}>Sipariş no</span>
                <span className={d.v}>{oz.nos.join(', ')}</span>
                <span className={d.k}>Kalem / Tutar</span>
                <span className={d.v}>
                  {formatNumber(oz.lines)} kalem · {formatTRY(oz.amount)} <i>(KDV hariç)</i>
                </span>
                {oz.erp_nos.length > 0 && (
                  <>
                    <span className={d.k}>ERP fişi</span>
                    <span className={d.v}>{oz.erp_nos.join(', ')}</span>
                  </>
                )}
              </div>

              <div className={d.tables}>
                <span className={d.tablesTitle}>Silinecek kayıtlar</span>
                <ul>
                  {TABLOLAR.map(([k, etiket]) => (
                    <li key={k}><span>{etiket}</span><b>{formatNumber(oz.tables[k] || 0)}</b></li>
                  ))}
                </ul>
                <p className={d.keep}>
                  <Info size={13} />
                  <span>Stok hareketleri korunur. Silinen sipariş hiçbir ekranda görünmez;
                    kopyası yalnızca geri getirme için saklanır.</span>
                </p>
              </div>

              {kritik ? (
                <div className={d.danger}>
                  <ShieldAlert size={18} />
                  <div>
                    <b>{oz.transferred
                      ? "Bu sipariş ERP'ye aktarılmış."
                      : "Bu sipariş şu an ERP'ye aktarılıyor olabilir."}</b>
                    <span>
                      Buradan silmek {oz.erp_nos.length
                        ? `ERP'deki ${oz.erp_nos.join(', ')} fişini`
                        : 'ERP / Bosch tarafındaki kaydı'} iptal etmez; gerekiyorsa orada ayrıca iptal edin.
                      {oz.in_transfer && !oz.transferred
                        ? " Aktarım tamamlanırsa ERP'de fiş oluşabilir; silmeden sonra ERP'yi kontrol edin."
                        : ''}
                    </span>
                    <label className={d.confirm}>
                      <span>Onaylamak için sipariş numarasını yazın: <b>{oz.nos[0]}</b></span>
                      <input
                        ref={inputRef} className={s.input} value={yazilan}
                        onChange={(e) => setYazilan(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') sil(); }}
                        placeholder={oz.nos[0]} autoComplete="off" spellCheck={false}
                        disabled={siliyor}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className={d.warn}>
                  <AlertTriangle size={16} />
                  <span>Bu işlem ekrandan geri alınamaz. Sipariş müşteri ve yönetim ekranlarından kaldırılır.</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className={s.foot}>
          <button type="button" className={s.cancelBtn} onClick={onKapat} disabled={siliyor}>Vazgeç</button>
          <button type="button" className={d.delBtn} onClick={sil}
                  disabled={!oz || siliyor || !numaraTamam}>
            {siliyor ? <Loader2 size={16} className={s.spin} /> : <Trash2 size={16} />}
            {siliyor ? 'Siliniyor…' : 'Siparişi Sil'}
          </button>
        </div>
      </div>
    </div>
  );
}
